import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { Session, User } from '@supabase/supabase-js';

import { SupabaseClientService } from '../client/supabase-client.service';
import { UserProfileService } from './user-profile.service';
import { UserActivityLog, UserProfile } from '@app-libs/model';
import { AuthCore } from '@app-libs/core';
import { ActivityTrackingService } from '../client/activity-tracking.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends AuthCore implements OnDestroy {
  // We store null, false, or a User object
  private currentUser = new BehaviorSubject<User | false | null>(null);

  // For user profile, we store a defined interface or null
  private userProfile = new BehaviorSubject<UserProfile | null>(null);

  // Indicates whether the initial auth check is done
  private initialAuthCheckComplete = new BehaviorSubject<boolean>(false);

  // For development or testing
  bypassAuth = false;

  private lastToken: string | null = null;
  private authStateSubscription?: { data: { subscription: { unsubscribe: () => void } } };
  private subscriptions = new Subscription();

  constructor(
    supabaseService: SupabaseClientService,
    private userProfileService: UserProfileService,
    private activityService: ActivityTrackingService // Import to start logging user activity
  ) {
    super(supabaseService);

    // Subscribe to auth state changes
    this.authStateSubscription = this.getSupabase()?.auth.onAuthStateChange(async (event, session) => {
      console.debug('[Auth] State Change:', event, session?.user?.email);

      const token = session?.access_token ?? null;
      const user: User | undefined = session?.user;

      // if token is the same as lastToken, we skip processing to avoid unnecessary updates
      if (token === this.lastToken && event === 'SIGNED_IN') return;

      this.lastToken = token;

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        this.currentUser.next(user || false);
      } else {
        // SIGNED_OUT or other events
        this.currentUser.next(false);
        this.lastToken = null;
      }

      this.initialAuthCheckComplete.next(true);
      this.trackUserActivity(user, event);
    });

    // this.currentUser.subscribe((user) => {
    //   // console.debug('currentUser.subscribe Current User:', user);
    //   this.loadUserProfile(user);
    // });

    // Subscribe to user updates to refresh profile
    this.subscriptions.add(
      this.currentUser
        .pipe(
          distinctUntilChanged((prev, curr) => 
            typeof prev === 'object' && typeof curr === 'object' && prev?.id === curr?.id
          )
        )
        .subscribe((user) => {
          this.loadUserProfile(user);
        })
    );
  }

  private async loadUserProfile(user: User | false | null): Promise<void> {
    // console.debug('[loadUserProfile] User:', user);
    if (user) {
      await this.userProfileService.loadUserProfile(user.id);
    } else {
      this.userProfile.next(null);
    }
  }

  /**
   * Returns an Observable of whether the user is authenticated.
   * If on the server, we emit undefined.
   */
  isAuthenticatedWait(): Observable<boolean> {
    // console.log('Checking auth status...');

    const user = this.currentUser.asObservable().pipe(
      filter(() => this.initialAuthCheckComplete.value),
      map((user) => user !== null && user !== false)
    );

    return user;
  }

  /**
   * Waits for the initial auth check to complete before continuing.
   */
  async waitForInitialAuthCheck(): Promise<void> {
    console.debug('Waiting for initial auth check...');
    if (!this.initialAuthCheckComplete.value) {
      await firstValueFrom(
        this.initialAuthCheckComplete
          .asObservable()
          .pipe(filter((complete) => complete))
      );
    }
  }

  // ---- Accessors ----

  /**
   * Returns currentUser as an Observable. Could be null, false, or a User object.
   */
  getCurrentUser(): Observable<User | false | null> {
    return this.currentUser.asObservable();
  }

  /**
   * Returns the current user ID if available, otherwise null.
   */
  getCurrentUserId(): string | null {
    const user = this.currentUser.value;
    if (user && typeof user !== 'boolean') {
      return user.id;
    }
    return null;
  }

  /**
   * Returns the current user email if available, otherwise null.
   */
  getCurrentUserEmail(): string | null {
    const user = this.currentUser.value;
    if (user && typeof user !== 'boolean') {
      return user.email || null;
    }
    return null;
  }

  /**
   * Returns the user's profile as an Observable.
   */
  getUserProfile(): Observable<UserProfile> {
    return this.userProfileService
      .getUserProfile()
      .pipe(filter((profile): profile is UserProfile => profile !== null));
  }

  /**
   * Returns the user's profile as an Observable.
   */
  async getProfile(): Promise<UserProfile> {
    return firstValueFrom(
      this.getUserProfile().pipe(map((profile) => profile))
    );
  }

  async isSuperAdmin(): Promise<boolean> {
    const profile = await firstValueFrom(this.getUserProfile());
    return profile.roles?.includes(this.ROLE_SUPERADMIN) || false;
  }

  async isAdmin(): Promise<boolean> {
    const profile = await firstValueFrom(this.getUserProfile());
    return profile.roles?.includes(this.ROLE_ADMIN) || false;
  }

  /**
   * Reactive method to check if the current user is a superadmin
   */
  isSuperAdmin$(): Observable<boolean> {
    return this.getUserProfile().pipe(
      map(profile => profile?.roles?.includes(this.ROLE_SUPERADMIN) || false),
      distinctUntilChanged()
    );
  }

  /**
   * Reactive method to check if the current user is an admin
   */
  isAdmin$(): Observable<boolean> {
    return this.getUserProfile().pipe(
      map(profile => profile?.roles?.includes(this.ROLE_ADMIN) || false),
      distinctUntilChanged()
    );
  }

  /**
   * Reactive method to check if the current user has a specific role
   */
  hasRole$(role: string): Observable<boolean> {
    return this.getUserProfile().pipe(
      map(profile => profile?.roles?.includes(role) || false),
      distinctUntilChanged()
    );
  }

  /**
   * Reactive method to check if the current user has any of the specified roles
   */
  hasAnyRole$(roles: string[]): Observable<boolean> {
    return this.getUserProfile().pipe(
      map(profile => {
        const userRoles = profile?.roles || [];
        return roles.some(role => userRoles.includes(role));
      }),
      distinctUntilChanged()
    );
  }

  /**
   * Get all roles for the current user as an observable
   */
  getUserRoles$(): Observable<string[]> {
    return this.getUserProfile().pipe(
      map(profile => profile?.roles || []),
      distinctUntilChanged((prev, curr) => 
        JSON.stringify(prev) === JSON.stringify(curr)
      )
    );
  }

  async trackUserActivity(
    user: User | undefined,
    event: string
  ): Promise<UserActivityLog> {
    if (!user) {
      return {} as UserActivityLog;
    }
    return this.activityService.createUserActivity(
      user.id,
      'supabase.auth',
      event,
      {
        user: user.email,
      }
    );
  }

  async getSession(): Promise<Session | null> {
    return this.supabaseService.getSession();
  }

  async getAccessToken(): Promise<string | null> {
    return this.getSession().then((session) => {
      return session?.access_token || null;
    });
  }

  ngOnDestroy(): void {
    // Remove auth state listener
    if (this.authStateSubscription) {
      this.authStateSubscription.data.subscription.unsubscribe();
    }
    
    // Clean up other subscriptions
    this.subscriptions.unsubscribe();
  }
}
